/**
 * The applying screen: while the worker fills the employer's form, a live
 * card narrates each section as it goes ("Filling my information… 12 fields
 * filled"); once a screen is filled, its fields appear below, grouped the way
 * a person reads an application — personal information, work experience,
 * education, skills, documents, application questions, voluntary
 * disclosures, agreements — with the value sent, who supplied it, and a
 * "fallback" mark on answers given to keep the form moving.
 *
 * Both parts read `application.appliedForm` and the timeline events the
 * page already polls every two seconds while a run is working.
 */
import { useMemo } from 'react'

import { Badge } from '@/components/ui/Feedback'
import { cn } from '@/lib/utils'
import type { AppliedField, AppliedForm, AppliedSection, ApplicationDetail, ApplicationEvent } from '@/types/api'

import { Spinner } from './QuestionField'

const WORKING = new Set(['queued', 'in_flight'])

const SOURCE_LABEL: Record<string, string> = {
  profile: 'Profile',
  resume: 'Resume',
  previous_answer: 'Remembered',
  ai: 'AI',
  user: 'You',
  fallback: 'Fallback',
  form: 'Form',
}

/** Steps shown in the live card, from the events the run reported. */
interface StepProgress {
  index: number
  step: string
  fields: number | null
  seconds: number | null
  done: boolean
}

function stepsFrom(events: ApplicationEvent[]): StepProgress[] {
  const byIndex = new Map<number, StepProgress>()
  for (const event of events) {
    const payload = event.payload ?? {}
    const step = typeof payload.step === 'string' ? payload.step : null
    const index = typeof payload.index === 'number' ? payload.index : null
    if (step === null || index === null) continue
    if (event.eventType === 'section_filling') {
      byIndex.set(index, { index, step, fields: null, seconds: null, done: false })
    } else if (event.eventType === 'section_filled') {
      const ms = typeof payload.ms === 'number' ? payload.ms : null
      byIndex.set(index, { index, step, fields: typeof payload.fields === 'number' ? payload.fields : null, seconds: ms === null ? null : Math.round(ms / 1000), done: true })
    }
  }
  return [...byIndex.values()].sort((a, b) => a.index - b.index)
}

/** What the run is doing right now, in its own words (the latest event with a message). */
function narration(application: ApplicationDetail): string {
  const events = application.events
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]!
    if (event.eventType === 'screen_filled' || event.eventType === 'screen_read') continue
    if (event.message) return event.message
  }
  return application.status === 'queued' ? 'Waiting for a free browser…' : 'Opening the employer’s site…'
}

/** The live card: the run's current section, the sections done, and how many fields are in so far. */
export function ApplyingPanel({ application }: { application: ApplicationDetail }) {
  const steps = useMemo(() => stepsFrom(application.events), [application.events])
  const form = application.appliedForm ?? null
  const filled = form?.stats.answered ?? 0
  const current = steps.find((step) => !step.done) ?? null
  return (
    <section className="card overflow-hidden" aria-live="polite">
      <header className="flex items-start justify-between gap-3 px-4 py-4 sm:px-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-info/10 text-info">
            <Spinner />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-ink">
              {application.status === 'queued' ? 'Waiting to apply' : 'Submitting web application'}
            </h2>
            <p className="mt-0.5 truncate text-[13px] text-ink-muted">{narration(application)}</p>
          </div>
        </div>
        <p className="num shrink-0 text-right text-[12.5px] text-ink-muted">
          <span className="block text-[18px] font-semibold leading-tight text-ink">{filled}</span>
          field{filled === 1 ? '' : 's'} filled
        </p>
      </header>
      {steps.length > 0 && (
        <ol className="border-t border-line px-4 py-3 sm:px-5">
          {steps.map((step) => (
            <li key={`${step.index}-${step.step}`} className="flex items-center gap-3 py-1.5 text-[13.5px]">
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px]',
                  step.done ? 'bg-success/12 text-success' : 'bg-info/10 text-info',
                )}
              >
                {step.done ? '✓' : <Spinner />}
              </span>
              <span className={cn('min-w-0 flex-1 truncate', step.done ? 'text-ink' : 'font-medium text-ink')}>
                {step.step}
              </span>
              {step.done && (
                <span className="num shrink-0 text-[12px] text-ink-faint">
                  {step.fields !== null && `${step.fields} field${step.fields === 1 ? '' : 's'}`}
                  {step.seconds !== null && ` · ${step.seconds} s`}
                </span>
              )}
              {!step.done && current?.index === step.index && <span className="shrink-0 text-[12px] text-info">Filling…</span>}
            </li>
          ))}
        </ol>
      )}
      {form && form.sections.length > 0 && (
        <div className="border-t border-line">
          <SectionList form={form} compact />
        </div>
      )}
    </section>
  )
}

/** The full applied form, once the run is over (or as far as it got). */
export function AppliedFormPanel({ application }: { application: ApplicationDetail }) {
  const form = application.appliedForm ?? null
  if (!form || form.sections.length === 0) return null
  const working = WORKING.has(application.status)
  return (
    <section className="card overflow-hidden">
      <header className="flex items-baseline justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
        <div>
          <h2 className="text-[14.5px] font-semibold text-ink">{working ? 'Form so far' : 'What we applied with'}</h2>
          <p className="mt-0.5 text-[12.5px] text-ink-muted">
            {form.stats.answered} of {form.stats.fields} field{form.stats.fields === 1 ? '' : 's'} filled
            {form.stats.fallbacks > 0 && ` · ${form.stats.fallbacks} fallback${form.stats.fallbacks === 1 ? '' : 's'}`}
          </p>
        </div>
        {form.steps.length > 0 && (
          <span className="num text-[12px] text-ink-faint">
            {form.steps.length} screen{form.steps.length === 1 ? '' : 's'} ·{' '}
            {Math.round(form.steps.reduce((sum, step) => sum + (step.ms ?? 0), 0) / 1000)} s
          </span>
        )}
      </header>
      <SectionList form={form} />
    </section>
  )
}


function SectionList({ form, compact = false }: { form: AppliedForm; compact?: boolean }) {
  return (
    <div className="divide-y divide-line">
      {form.sections.map((section) => (
        <SectionBlock key={section.id} section={section} compact={compact} />
      ))}
    </div>
  )
}

function SectionBlock({ section, compact }: { section: AppliedSection; compact: boolean }) {
  const groups = useMemo(() => groupEntries(section), [section])
  return (
    <div className="px-4 py-3 sm:px-5">
      <h3 className="flex items-baseline justify-between gap-3">
        <span className="text-[13.5px] font-semibold text-ink">{section.title}</span>
        <span className="num text-[11.5px] text-ink-faint">
          {section.fields.length} field{section.fields.length === 1 ? '' : 's'}
        </span>
      </h3>
      <div className={cn('mt-2', compact ? 'space-y-2' : 'space-y-3')}>
        {groups.map((group) => (
          <div key={group.key}>
            {group.title && <p className="label-eyebrow mb-1.5 mt-2 text-ink-muted">{group.title}</p>}
            <dl className={cn('grid gap-x-6', compact ? 'gap-y-1.5' : 'gap-y-3', !compact && 'sm:grid-cols-2')}>
              {group.fields.map((field) => (
                <FieldRow key={field.key} field={field} compact={compact} />
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Work and education entries get a sub-heading each ("Work Experience 1 · EPAM Systems"). */
function groupEntries(section: AppliedSection): Array<{ key: string; title: string | null; fields: AppliedField[] }> {
  if (section.id !== 'experience' && section.id !== 'education') return [{ key: 'all', title: null, fields: section.fields }]
  const groups = new Map<string, AppliedField[]>()
  for (const field of section.fields) {
    const key = field.entry ?? ''
    groups.set(key, [...(groups.get(key) ?? []), field])
  }
  let n = 0
  return [...groups.entries()].map(([entry, fields]) => {
    const summary = fields.every((field) => field.type === 'entry')
    n += summary ? 0 : 1
    return { key: entry || `entry-${n}`, title: summary ? null : entry ? `${section.title} ${n} · ${entry}` : fields.length > 1 ? `${section.title} ${n}` : null, fields }
  })
}

const SHOWN_OPTIONS = 12

function FieldRow({ field, compact }: { field: AppliedField; compact: boolean }) {
  const long = field.optionsCount > SHOWN_OPTIONS || field.options.length < field.optionsCount
  const choices = !long && field.options.length > 0 && field.options.length <= SHOWN_OPTIONS && field.type !== 'file'
  const selected = new Set((field.value ?? '').split(/\s*[,;]\s*/).map((v) => v.trim().toLowerCase()).filter(Boolean))
  return (
    <div className="min-w-0">
      <dt className="flex items-baseline gap-2 text-[11.5px] font-medium uppercase tracking-wide text-ink-muted">
        <span className="min-w-0 truncate" title={field.label}>
          {field.label || field.key}
          {field.required && <span className="text-danger"> *</span>}
        </span>
        {field.fallback && (
          <Badge tone="warning" dot>
            fallback
          </Badge>
        )}
        {field.source && !field.fallback && !compact && (
          <span className="shrink-0 text-[10.5px] font-normal normal-case tracking-normal text-ink-faint" title={field.rationale ?? undefined}>
            {SOURCE_LABEL[field.source] ?? field.source}
          </span>
        )}
      </dt>
      <dd className="mt-1 min-w-0">
        {choices ? (
          <ul className="flex flex-wrap gap-1.5">
            {field.options.map((option) => {
              const on = selected.has(option.trim().toLowerCase())
              return (
                <li
                  key={option}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-pill border px-2 py-0.5 text-[12.5px]',
                    on ? 'border-ink bg-ink text-oncolor' : 'border-line text-ink-faint',
                  )}
                >
                  <span aria-hidden="true" className={cn('h-2 w-2 rounded-full', on ? 'bg-oncolor' : 'bg-line-strong')} />
                  {option}
                </li>
              )
            })}
          </ul>
        ) : (
          <p
            className={cn(
              'field-box min-h-[34px] whitespace-pre-wrap break-words px-3 py-1.5 text-[13.5px]',
              field.value ? 'text-ink' : 'text-ink-faint',
            )}
          >
            {field.value ?? (field.fillError ? 'Not filled' : '—')}
          </p>
        )}
        {long && field.value && (
          <p className="mt-1 text-[11.5px] text-ink-faint">Selected from {field.optionsCount} options</p>
        )}
        {field.fillError && <p className="mt-1 text-[12px] text-danger">{field.fillError}</p>}
      </dd>
    </div>
  )
}
